import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { capitalize } from "lodash";
import classNames from "classnames";
import { broadcastLogout } from "common/utils/coordinatedLogout";
import {
  Modal,
  ScreenSizeRender,
  Tab,
  Tabs,
  SlideOutDrawer,
  SideMenuItem,
  SideNav,
  Card,
  JSQLogo,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { type ArenaContent, useAppContext } from "apps/portal/Portal/common/AppContext";
import { NavbarContainer, NavbarDropdown, NavbarLogo } from "@jsq/ds/layouts";

import { getLpPortalTermsAndPrivacyPath, getLpPortalTouBasePath } from "common/helpers/url";
import { useGetSessionStatusQuery } from "common/types/mainHooks";
import { buildSrcSetString } from "common/utils/img";

import ContextSwitcher from "apps/portal/Portal/common/ContextSwitcher";
import HelpModal from "apps/portal/Portal/common/HelpModal";
import { useUpscope } from "@upscopeio/react";
import { ProfileDropDown } from "./ProfileDropDown";
import { ContextSwitcherSvg } from "./ContextSwitcherSvg";
import { useFeaturesContext } from "../contexts/FeaturesContext";
import styles from "./styles.scss";

type Props = {
  baseUrl: string;
  isNotAuthenticated?: boolean;
  navItems: string[];
  loginQueryParam?: string;
  customLoginLocationState?: Record<string, any>;
  isDisplayingPreviewBanner?: boolean;
  isStaffPreview?: boolean;
  customArenaContent?: Pick<ArenaContent, "headerLogoUrl" | "name" | "headerLogoUrl2x">;
};

const TermsAndCondition = ({ unifiedPortal }: { unifiedPortal: boolean | undefined }) => {
  const privacyPolicyUrl = unifiedPortal
    ? getLpPortalTermsAndPrivacyPath()
    : getLpPortalTouBasePath();

  return (
    <Card className={styles.NavBar__borderlessCard}>
      <div className={styles.NavBar__termsCopyrightWrapper}>
        <a target="_blank" rel="noopener noreferrer" href={privacyPolicyUrl}>
          <span data-cy="sidebar-terms-and-privacy-nav">Terms and privacy</span>
        </a>
        <p>© {new Date().getFullYear()} Juniper Square, Inc.</p>
      </div>
    </Card>
  );
};

export const NavBar: React.FunctionComponent<Props> = ({
  baseUrl,
  isNotAuthenticated,
  navItems,
  loginQueryParam,
  customLoginLocationState,
  isDisplayingPreviewBanner,
  isStaffPreview,
  customArenaContent,
}) => {
  const [showHelpModal, setShowHelpModal] = useState(false);
  const { data: sessionData, loading } = useGetSessionStatusQuery({
    skip: isStaffPreview || isNotAuthenticated,
  });
  const isSessionSpoofed = sessionData?.session?.spoofed;
  const { currentArenaContent, managerArenas, user, onlyOneGp, getLpPortalSettingsPath } =
    useAppContext();
  const shouldShowContextSwitcher =
    !loading &&
    !isSessionSpoofed &&
    !isNotAuthenticated &&
    managerArenas.length > 0 &&
    !isStaffPreview;
  const singleManager = !!currentArenaContent;

  const location = useLocation();
  // We need this parameter to navigate arena login page in activation flow
  const nonAuthLink = loginQueryParam || `?path=${location.pathname}`;
  const extendedNavItems = navItems.concat("position", "settings");
  const currentPageNavItem = extendedNavItems.find((navItem) =>
    location.pathname?.startsWith(`${baseUrl}/${navItem}`),
  );
  const currentTopLevelPageNavItem =
    currentPageNavItem === "position" ? "investments" : currentPageNavItem;

  const [showMobileDrawer, setShowMobileDrawer] = React.useState(false);
  const [showContextSwitcher, setShowContextSwitcher] = React.useState(false);
  const { lpZendeskFeatureFlag, unifiedPortal, enableCoBrowseFeature } = useFeaturesContext();

  // Ensure that there is a user - so we don't show the help icon in the activation flow.
  const showHelpLink = lpZendeskFeatureFlag && user;
  const { Upscope } = useUpscope();

  useEffect(() => {
    if (enableCoBrowseFeature && Upscope && !isStaffPreview) {
      Upscope("init", {
        apiKey: "SqzNmzzieU",
      });
    }
  }, [Upscope, enableCoBrowseFeature, isStaffPreview]);

  useEffect(() => {
    if (enableCoBrowseFeature && Upscope && user?.emailAddress && !isStaffPreview) {
      Upscope?.("updateConnection", {
        apiKey: "SqzNmzzieU",
        uniqueId: user?.emailAddress,
        identities: [user?.fullName, user?.emailAddress],
      });
    }
  }, [Upscope, user, enableCoBrowseFeature, isStaffPreview]);

  const renderDesktopNavItems = () => {
    return (
      <div>
        <Tabs hasBottomBorder={false}>
          {navItems.map((navItem) => (
            <Tab
              active={currentTopLevelPageNavItem === navItem}
              text={capitalize(navItem)}
              to={`${baseUrl}/${navItem}`}
              data-cy={`${navItem}-tab`}
              key={navItem}
              className={styles.NavBarDesktopTab}
            />
          ))}
        </Tabs>
        {showHelpLink && (
          <div
            role="presentation"
            className={classNames(
              styles.NavBar__IconTab,
              styles.NavBarDesktopTab,
              styles.NavBarDesktopDropdownIcon,
            )}
            onClick={() => setShowHelpModal(!showHelpModal)}
            onKeyDown={() => setShowHelpModal(!showHelpModal)}
            data-cy="global-help-nav-container"
          >
            <Icon type="question-circle" variant="outline" data-cy="global-help-nav" />
            &nbsp;Help
          </div>
        )}
        {!isSessionSpoofed && (
          <NavbarDropdown
            position="bottomRight"
            className={classNames(styles.NavBar__IconTab, styles.NavBarDesktopTab)}
            actionItem={<Icon type="cog" className={styles.NavBarDesktopDropdownIcon} />}
            data-cy="global-logout-nav"
            dropDownClassName={styles.NavBar__Dropdown}
            role="navigation"
            aria-label="User settings"
          >
            <ProfileDropDown
              userName={user?.fullName}
              userEmail={user?.emailAddress}
              singleManager={singleManager}
              arenaDomain={singleManager ? currentArenaContent.arenaDomain : undefined}
              onlyOneGp={onlyOneGp}
            />
          </NavbarDropdown>
        )}
      </div>
    );

    // if desktop, render with help modal when needed and profile settings
  };

  const getBrandUrl = () => {
    if (currentArenaContent) {
      return `${baseUrl}/home`;
    }
    if (isStaffPreview) {
      return "/dashboard";
    }
    return isNotAuthenticated ? `/login/${nonAuthLink}` : `${baseUrl}/home`;
  };

  const brand = () => {
    const arenaContent = customArenaContent ?? currentArenaContent;
    if (arenaContent) {
      // Single Manager shows the GP's Icon

      return arenaContent.headerLogoUrl ? (
        <img
          srcSet={buildSrcSetString([
            { url: arenaContent.headerLogoUrl },
            { url: arenaContent.headerLogoUrl2x, size: "2x" },
          ])}
          className={styles.NavBarBrandImage__image}
          alt={arenaContent.name}
        />
      ) : (
        <h3 className="h3" data-testid="navbar-home-link">
          {arenaContent.name}
        </h3>
      );
    }

    // Checking for isNotAuthenticated mainly for redirect from TOU
    return <JSQLogo maxWidth={152} maxHeight={19} />;
  };

  const isHomepage = /\/(lp|i\/(.*?))\/home/.test(location.pathname);

  return (
    <NavbarContainer
      className={classNames(
        styles.NavbarContainer,
        isDisplayingPreviewBanner && styles.NavbarContainer__withPreviewBanner,
      )}
    >
      <ScreenSizeRender small medium>
        <SlideOutDrawer
          isShowing={showMobileDrawer}
          headerContent={
            <NavbarLogo url={getBrandUrl()} data-cy="JSQLogo" isExternalLink={isStaffPreview}>
              {brand()}
            </NavbarLogo>
          }
          onClose={() => {
            setShowMobileDrawer(false);
          }}
        >
          {!isNotAuthenticated ? (
            <>
              <SideNav
                isBorderless
                sideNavItems={navItems.map((navItem) => {
                  return {
                    title: capitalize(navItem),
                    to: `${baseUrl}/${navItem}`,
                    exact: true,
                  };
                })}
              />
              {!isSessionSpoofed && (
                <>
                  <hr />
                  <Card data-cy="nav-card" className={styles.NavBar__borderlessCard}>
                    {shouldShowContextSwitcher && (
                      <SideMenuItem
                        title="Switch investor portal"
                        onClick={() => {
                          setShowContextSwitcher(true);
                        }}
                        isBorderless
                      />
                    )}
                    {showHelpLink && (
                      <SideMenuItem
                        title="Help"
                        isBorderless
                        onClick={() => setShowHelpModal(!showHelpModal)}
                      />
                    )}
                    <SideMenuItem
                      title="Settings"
                      icon={singleManager && !onlyOneGp ? "external-link" : undefined}
                      to={getLpPortalSettingsPath()}
                      isBorderless
                    />
                    <SideMenuItem
                      title="Log out"
                      isBorderless
                      onClick={() => {
                        broadcastLogout();
                        window.location.assign(
                          singleManager && currentArenaContent
                            ? `/logout?arena=${currentArenaContent.arenaDomain}`
                            : "/logout",
                        );
                      }}
                    />
                  </Card>
                </>
              )}
              <hr />
              <TermsAndCondition unifiedPortal={unifiedPortal} />
            </>
          ) : (
            <div className={styles.NavBar__unauthenticatedText}>
              <p className="text-md inset-md-y">Have a Juniper Square account? </p>
              <p className="text-md" data-cy="unauth-signin-text">
                {isStaffPreview ? (
                  <a href="/dashboard">Sign in</a>
                ) : (
                  <Link
                    to={{
                      pathname: `/login`,
                      search: nonAuthLink,
                      state: customLoginLocationState ?? location.state,
                    }}
                  >
                    Sign in
                  </Link>
                )}
              </p>
            </div>
          )}
        </SlideOutDrawer>
        {shouldShowContextSwitcher && (
          <Modal
            show={showContextSwitcher}
            onHide={() => {
              setShowContextSwitcher(false);
            }}
          >
            <Modal.Header closeButton>
              <h2 data-cy="help-modal-title">Switch investor portal</h2>
            </Modal.Header>
            <ContextSwitcher />
          </Modal>
        )}
        <div className={styles.NavBar__mobileContainer}>
          <button
            data-cy="hamburger-button"
            className={styles.NavBar__hamburger}
            onClick={() => setShowMobileDrawer(!showMobileDrawer)}
            aria-label="Toggle navigation menu"
          >
            {/*
                  Couldn't accomplish this with pseudo-elements so we're just using divs to style the hamburger.
                  Ignoring in prettier for readability.
                */}
            {/* prettier-ignore */}
            <div className={styles.NavBar__hamburgerInner}><div /><div /><div /></div>
          </button>
          <NavbarLogo url={getBrandUrl()} data-cy="JSQLogo" isExternalLink={isStaffPreview}>
            {isHomepage ? brand() : capitalize(currentTopLevelPageNavItem) || brand()}
          </NavbarLogo>
          <div className={styles.NavBar__inverseHamburger} />
        </div>
      </ScreenSizeRender>

      <ScreenSizeRender large>
        <div className={styles.NavBar__desktopContainer}>
          <div>
            {shouldShowContextSwitcher && (
              <NavbarDropdown
                position="bottomLeft"
                actionItem={<ContextSwitcherSvg />}
                buttonClassName={styles.NavbarNew__DropdownButton}
                dropDownClassName={styles.NavbarNew__Dropdown}
                data-cy="context-switcher"
                data-testid="context-switcher"
              >
                <ContextSwitcher />
              </NavbarDropdown>
            )}
            <NavbarLogo url={getBrandUrl()} data-cy="JSQLogo" isExternalLink={isStaffPreview}>
              {brand()}
            </NavbarLogo>
          </div>
          {isNotAuthenticated && (
            <p className="text-md align-right inset-md-y" data-cy="unauth-signin-text">
              <span className={styles.NavBar__unauthenticatedText}>
                Have a Juniper Square account?
              </span>{" "}
              {isStaffPreview ? (
                <a href="/dashboard">Sign in</a>
              ) : (
                <Link
                  to={{
                    pathname: `/login`,
                    search: nonAuthLink,
                    state: customLoginLocationState ?? location.state,
                  }}
                >
                  Sign in
                </Link>
              )}
            </p>
          )}
          {!isNotAuthenticated && renderDesktopNavItems()}
        </div>
      </ScreenSizeRender>

      {/* HelpModal appears in both mobile/tablet and desktop views*/}
      {showHelpLink && (
        <HelpModal show={showHelpModal} onHide={() => setShowHelpModal(false)} />
      )}
    </NavbarContainer>
  );
};
